import { useCallback, useEffect, useMemo, useState } from "react"

import { useParams } from "react-router-dom"

import { DocumentEditModal, API_MODULE_DOCUMENT } from "@l2r-front/l2r-app-doc"
import { CatalogItemEditModal, useGetViewCenter } from "@l2r-front/l2r-common"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { CampaignIcon, DeleteIcon, EditLocationIcon, ModeEditIcon, NoteAddRoundedIcon } from "@l2r-front/l2r-icons"
import { MAP_POINT_EDITOR_ID, useMapDispatchContext, useMapStateContext } from "@l2r-front/l2r-map"
import { useIsModuleAvailableForItem, useNetworksStateContext, useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { API_MODULE_CAMPAIGN, CampaignSelector, CampaignSelectorMobile } from "@l2r-front/l2r-shared-campaign"
import { MoreMenuWithChildren, useIsMobileDevice } from "@l2r-front/l2r-ui"
import { NavigationOrigins, useNavigateWithSearchParams, useShiftKeyShortcut } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { API_MODULE_HORIZONTAL_SIGNING } from "../../../../common/constants/modules"
import { DETAIL_TAB_PARAM_KEY } from "../../constants/tabKeys"
import { HorizontalSignAddDocumentFormPost } from "../../forms/HorizontalSignAddDocumentForm"
import { HorizontalSignAddFormPost } from "../../forms/HorizontalSignAddForm"
import { HorizontalSignDetailsFormPost } from "../../forms/HorizontalSignDetailsForm"
import { HorizontalSignDuplicateFormPost } from "../../forms/HorizontalSignDuplicateForm"
import { HorizontalSignLocationEditFormPost } from "../../forms/HorizontalSignLocationEditForm"
import { useHorizontalSignDelete } from "../../hooks/mutations/useHorizontalSignDelete"
import { useHorizontalSignUpdateCampaigns } from "../../hooks/mutations/useHorizontalSignUpdate"
import { useHorizontalSign } from "../../hooks/queries/horizontalSigning/useHorizontalSigns"
import { useHorizontalSignType } from "../../hooks/queries/horizontalSigning/useHorizontalSignTypes"

import { HorizontalSignDetailsSidebarContent } from "../HorizontalSignDetailsSidebarContent"

import * as Styled from "./HorizontalSignDetailsSidebar.styled"

export function HorizontalSignDetailsSidebar() {

    const [localizationModalOpen, setLocalizationModalOpen] = useState(false)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [documentAddModalOpen, setDocumentAddModalOpen] = useState(false)
    const [duplicateModalOpen, setDuplicateModalOpen] = useState(false)
    const [newSignLocation, setNewSignLocation] = useState(null)

    const { t } = useTranslation(I18N_NAMESPACE)
    const { setAlert } = useAlertsDispatchContext()
    const navigate = useNavigateWithSearchParams()
    const { signId, road } = useParams()
    const isMobile = useIsMobileDevice()

    const { data: sign, isLoading: isLoadingSign } = useHorizontalSign(signId)
    const { data: signType } = useHorizontalSignType(sign?.horizontal_sign_type, {}, { enabled: !!sign })
    const readOnly = useRoleIsReadOnly(sign?.sub_networks, API_MODULE_HORIZONTAL_SIGNING)
    const documentAvailable = useIsModuleAvailableForItem(sign?.sub_networks, API_MODULE_DOCUMENT)
    const campaignAvailable = useIsModuleAvailableForItem(sign?.sub_networks, API_MODULE_CAMPAIGN)
    const { resetMapBoundingBox, setMapPin } = useMapDispatchContext()
    const { storedBoundingBox } = useMapStateContext()
    const latLong = sign?.geometry.coordinates
    const [campaignIds, setCampaignIds] = useState(sign?.campaigns || [])
    const { selectedNetwork } = useNetworksStateContext()
    const getViewCenter = useGetViewCenter()

    useEffect(() => {
        setCampaignIds(sign?.campaigns || [])
    }, [sign])

    const title = useMemo(() => {
        return signType?.name ? signType.name : ""
    }, [signType])

    const navigateBack = useCallback(() => {
        setMapPin()
        navigate(`../${road}`, { paramsToDelete: [DETAIL_TAB_PARAM_KEY], navigationOrigin: NavigationOrigins.ROAD_FROM_BACK_DETAIL })
    }, [navigate, road, setMapPin])

    const deleteMutation = useHorizontalSignDelete(signId, {
        onSuccess: () => {
            setMapPin()
            navigate(`../${road}`)
        },
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.edit.delete.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.edit.delete.success"),
    })

    const updateMutation = useHorizontalSignUpdateCampaigns(signId, {}, {
        onSuccess: () => {
            setCampaignIds(prev => prev.filter(campaign => !campaignIds.some(c => c.uuid === campaign.uuid)))
        },
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.editCampaigns.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.editCampaigns.success"),
    })

    const handleDeleteSign = useCallback(() => {
        setAlert({
            alertTitle: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.edit.delete.confirmation", { itemName: signType?.name }),
            cancelText: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.edit.delete.cancel"),
            confirmText: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.edit.delete.confirm"),
            callback: () => {
                deleteMutation.mutate()
            },
            severity: "warning",
        })
    }, [signType, deleteMutation, setAlert, t])

    const handleUpdateAttachedCampaigns = useCallback((campaigns) => {
        const newCampaignsIds = campaigns || campaignIds
        const campaignsToAdd = newCampaignsIds.filter(itemCampaign => {
            return !sign?.campaigns?.some(signCampaign => signCampaign.uuid === itemCampaign.uuid)
        }).map(itemCampaign => ({
            campaign: itemCampaign.uuid,
            to_attach: true,
        })) || []

        const campaignsToRemove = sign?.campaigns?.filter(signCampaign => {
            return !newCampaignsIds.some(itemCampaign => itemCampaign.uuid === signCampaign.uuid)
        }).map(signCampaign => ({
            campaign: signCampaign.uuid,
            to_attach: false,
        })) || []

        if (campaignsToAdd.length === 0 && campaignsToRemove?.length === 0) {
            return
        }

        updateMutation.mutate({ campaign_assignments: campaignsToAdd.concat(campaignsToRemove) })
    }, [campaignIds, sign, updateMutation])

    const handleCampaignIdsChange = useCallback((campaignUuid) => {
        setCampaignIds(prevCampaignIds => {
            if (!prevCampaignIds?.some((campaign) => campaign.uuid === campaignUuid)) {
                return [...prevCampaignIds, { uuid: campaignUuid }]
            } else {
                return prevCampaignIds.filter((campaign) => campaign.uuid !== campaignUuid)
            }
        })
    }, [])

    const handleDetachCampaign = useCallback((campaignId) => {
        const newCampaignIds = campaignIds.filter(campaign => campaign.uuid !== campaignId)
        handleUpdateAttachedCampaigns(newCampaignIds)
    }, [campaignIds, handleUpdateAttachedCampaigns])

    const anyModalOpen = localizationModalOpen || editModalOpen || documentAddModalOpen || duplicateModalOpen || !!newSignLocation
    useShiftKeyShortcut("d", () => setDuplicateModalOpen(true), !!sign && !readOnly && !anyModalOpen)
    useShiftKeyShortcut("n", () => setNewSignLocation(getViewCenter()), !!sign && !readOnly && !anyModalOpen)
    useShiftKeyShortcut("e", () => setEditModalOpen(true), !!sign && !readOnly && !anyModalOpen)

    useEffect(() => {
        if (!localizationModalOpen && !duplicateModalOpen && !newSignLocation && storedBoundingBox?.[MAP_POINT_EDITOR_ID]) {
            resetMapBoundingBox(null, MAP_POINT_EDITOR_ID)
        }
    }, [localizationModalOpen, duplicateModalOpen, newSignLocation, resetMapBoundingBox, storedBoundingBox])


    if (!sign || isLoadingSign) {
        return null
    }

    return <>
        <Styled.BottomSheetContent id="horizontalSignDetails-sidebar"
            onBack={navigateBack}
            title={title}
            titleSideComponent={<MoreMenuWithChildren
                onClose={(confirmed = true) => confirmed && handleUpdateAttachedCampaigns()}
                menuConfig={[
                    {
                        displayed: !readOnly,
                        id: "edit-location",
                        label: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.edit.location"),
                        icon: <EditLocationIcon color="common/black_states" />,
                        props: {
                            onClick: () => setLocalizationModalOpen(true),
                        },
                    },
                    {
                        displayed: !readOnly,
                        id: "edit-properties",
                        label: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.edit.properties", { itemName: signType?.name }),
                        icon: <ModeEditIcon color="common/black_states" />,
                        props: {
                            onClick: () => setEditModalOpen(true),
                        },
                    },
                    {
                        displayed: documentAvailable && !readOnly,
                        id: "add-document",
                        label: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.addDocument"),
                        icon: <NoteAddRoundedIcon color="common/black_states" />,
                        props: {
                            onClick: () => setDocumentAddModalOpen(true),
                        },
                    },
                    {
                        children: {
                            component: isMobile ? CampaignSelectorMobile : CampaignSelector,
                            props: {
                                canApply: campaignIds.length !== sign.campaigns?.length,
                                campaignIds: campaignIds,
                                campaignType: API_MODULE_HORIZONTAL_SIGNING,
                                onChange: handleCampaignIdsChange,
                                onCancel: () => setCampaignIds(sign?.campaigns || []),
                                onAddCampaignClick: () => navigate(`/${selectedNetwork?.slug}/campaign/campaigns/`),
                            },
                        },
                        displayed: campaignAvailable && !readOnly,
                        id: "add-campaign",
                        label: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.addCampaign"),
                        icon: <CampaignIcon color="common/black_states" />,
                    },
                    {
                        displayed: !readOnly,
                        id: "delete-sign",
                        label: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.edit.delete.menu", { itemName: signType?.name }),
                        icon: <DeleteIcon color="common/black_states" />,
                        props: {
                            onClick: () => handleDeleteSign(),
                        },
                    },
                ]}
            />}
        >
            <HorizontalSignDetailsSidebarContent onDetachCampaign={handleDetachCampaign} />
        </Styled.BottomSheetContent>
        <CatalogItemEditModal
            FormComponent={HorizontalSignLocationEditFormPost}
            formTitle={t(I18N_NAMESPACE, "containers.horizontalSignLocationEditForm.title")}
            signId={signId}
            startingPosition={{ longitude: latLong[0], latitude: latLong[1] }}
            startingRoad={sign?.linear_location.road}
            open={localizationModalOpen}
            onClose={() => setLocalizationModalOpen(false)}
        />
        {newSignLocation && <CatalogItemEditModal
            formTitle={t(I18N_NAMESPACE, "containers.horizontalSignAddForm.title")}
            typeTitle={t(I18N_NAMESPACE, "containers.horizontalSignDetailsForm.type")}
            contentFieldLabel={t(I18N_NAMESPACE, "containers.horizontalSignDetailsForm.content")}
            FormComponent={HorizontalSignAddFormPost}
            startingPosition={newSignLocation}
            requirePositionChange
            open={!!newSignLocation}
            onClose={() => setNewSignLocation(null)}
        />}
        <CatalogItemEditModal
            FormComponent={HorizontalSignDuplicateFormPost}
            formTitle={t(I18N_NAMESPACE, "containers.horizontalSignDuplicateForm.title")}
            sign={sign}
            startingPosition={{ longitude: latLong[0], latitude: latLong[1] }}
            startingRoad={sign?.linear_location.road}
            open={duplicateModalOpen}
            onClose={() => setDuplicateModalOpen(false)}
        />
        <CatalogItemEditModal
            formTitle={t(I18N_NAMESPACE, "containers.horizontalSignDetailsForm.title")}
            typeTitle={t(I18N_NAMESPACE, "containers.horizontalSignDetailsForm.type")}
            contentFieldLabel={t(I18N_NAMESPACE, "containers.horizontalSignDetailsForm.content")}
            FormComponent={HorizontalSignDetailsFormPost}
            signId={sign?.uuid}
            open={editModalOpen}
            onClose={() => setEditModalOpen(false)}
        />
        {documentAddModalOpen && <DocumentEditModal
            open={documentAddModalOpen}
            onClose={() => setDocumentAddModalOpen(false)}
            FormComponent={HorizontalSignAddDocumentFormPost}
            signId={signId}
        />}
    </>
}